import Modal from "./Modal"
import { CardVariant } from "../../models/CardVariant"
import { CardVariantImage } from "../CardVariantImage/CardVariantImage";
import { ReactNode, useEffect, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { classNames } from "../../style/common";
import { KeyboardEvent } from "react"
import { CardVariantImageBanOverlay } from "../CardVariantImage/CardVariantImageBanOverlay";
import { AppState } from "../../appSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { CardVariantImageDarkenOverlay } from "../CardVariantImage/CardVariantImageDarkenOverlay";
import { TournamentFormat } from "../../models/TournamentFormat";

export type CardCarouselModalProps = {
    startingIndex: number,
    variants: CardVariant[],
    isOpen: boolean,
    format: TournamentFormat,
    onOpenChange: (isOpen: boolean) => void,
    onVariantChange?: (variant: CardVariant) => void,
    rightActions?: ReactNode,
    onKeyDown?: ((event: KeyboardEvent<HTMLDivElement>) => void) | undefined
}

export function CardCarouselModal(props: CardCarouselModalProps) {
    const [currentIndex, setCurrentIndex] = useState(props.startingIndex);
    const [variant, setVariant] = useState<CardVariant | null>(props.variants ? (props.variants[props.startingIndex] ?? null) : null);
    const [hasNext, setHasNext] = useState(props.startingIndex < (props.variants?.length ?? 0) - 1);
    const [hasPrevious, setHasPrevious] = useState(props.startingIndex > 0);

    const {
        formatRules,
        grandfatheredByOpus
    } = useSelector<RootState, AppState>(x => x.app);

    const next = () => {
        const newCurrentIndex = currentIndex + 1;
        updateIndex(newCurrentIndex);
    }

    const previous = () => {
        const newCurrentIndex = currentIndex - 1;
        updateIndex(newCurrentIndex);
    }

    const updateIndex = (newCurrentIndex: number) => {
        if (newCurrentIndex > props.variants.length - 1 || newCurrentIndex < 0) {
            return;
        }

        const maxIndex = props.variants.length - 1;
        const newHasNext = newCurrentIndex < maxIndex;
        const newHasPrevious = newCurrentIndex > 0;

        setCurrentIndex(newCurrentIndex);
        setHasNext(newHasNext);
        setHasPrevious(newHasPrevious);
        setVariant(props.variants[newCurrentIndex]);
        if (props.onVariantChange) {
            props.onVariantChange(props.variants[newCurrentIndex]);
        }
    }

    useEffect(() => {
        if (!props.variants.length || props.startingIndex > props.variants.length - 1) {
            return;
        }

        updateIndex(props.startingIndex);
    }, [props.startingIndex, props.variants]);

    if (props.startingIndex < 0 || !props.variants?.length || !variant) {
        return <></>;
    }

    return <>
        <Modal
            isOpen={props.isOpen}
            ignoreNavbarDodge={true}
            onOpenChange={(newIsOpen) => props.onOpenChange(newIsOpen)}
            onKeyDown={(event) => {
                if (event.code === "ArrowLeft" && hasPrevious) {
                    previous();
                    return;
                }

                if (event.code === "ArrowRight" && hasNext) {
                    next();
                    return;
                }

                props.onKeyDown?.(event);
            }}
            classNames={{
                dialogPanel: "!p-0 !bg-transparent rounded-xl !shadow-none !overflow-visible",
                modalTransitionContainer: "!max-w-[450px] absolute left-0 lg:left-72 right-0 mx-auto items-center",
                mainContainer: "!mt-0"
            }}
        >
            <div className="absolute top-0 bottom-[45%] md:left-[-56px] pl-2 md:pl-0 flex flex-col justify-end overflow">
                <button
                    aria-label="Previous"
                    title="Previous"
                    className={classNames(
                        "z-[200] theme-dark text-contrast-primary bg-primary hover:bg-secondary disabled:bg-muted disabled:cursor-not-allowed rounded-md p-2 border border-color-border/50"
                    )}
                    disabled={!hasPrevious}
                    onClick={() => previous()}>
                    <ChevronLeftIcon className="w-8 h-8" />
                </button>
            </div>
            <CardVariantImage
                variant={variant}
                overlay={
                    <>
                        <CardVariantImageBanOverlay
                            className="z-[11]"
                            variant={variant}
                            formatRules={formatRules}
                            tournamentFormat={props.format ?? "Standard"}
                            grandfatheredByOpus={grandfatheredByOpus}
                            overlay={<CardVariantImageDarkenOverlay className="z-10" />} />
                    </>
                } />
            <div className="absolute top-0 bottom-[45%] right-0 md:right-[-56px] pr-2 md:pr-0 flex flex-col justify-end">
                {
                    !!props.rightActions &&
                    props.rightActions
                }

                <button
                    aria-label="Next"
                    title="Next"
                    className="z-[200] theme-dark text-contrast-primary bg-primary hover:bg-secondary disabled:bg-muted disabled:cursor-not-allowed rounded-md p-2 border border-color-border/50"
                    disabled={!hasNext}
                    onClick={() => next()}>
                    <ChevronRightIcon className="w-8 h-8" />
                </button>
            </div>
        </Modal>
    </>
}